<template>
  <v-container fluid class="pa-4">
    <v-card elevation="2">
      <!-- Top bar: search, filter, export, new -->
      <v-toolbar flat class="bg-white px-4 py-3">
        <v-row align="center" justify="space-between" no-gutters>
          <!-- Search -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              density="comfortable"
              variant="outlined"
              hide-details
              append-inner-icon="mdi-magnify"
              label="Search transactions..."
              @input="getTransactions"
            />
          </v-col>

          <!-- Status filter chips -->
          <v-col cols="12" sm="4" class="text-center">
            <v-chip-group v-model="statusFilter" row @change="getTransactions">
              <!-- ALL -->
              <v-chip
                value=""
                :color="statusFilter === '' ? 'primary' : 'grey lighten-2'"
                text-color="white"
              >
                All
              </v-chip>

              <!-- COMPLETED -->
              <v-chip
                value="completed"
                :color="
                  statusFilter === 'completed' ? 'success' : 'grey lighten-2'
                "
                text-color="white"
              >
                Completed
              </v-chip>

              <!-- PENDING -->
              <v-chip
                value="pending"
                :color="
                  statusFilter === 'pending' ? 'warning' : 'grey lighten-2'
                "
                text-color="white"
              >
                Pending
              </v-chip>
            </v-chip-group>
          </v-col>

          <!-- Export / New -->
          <v-col cols="12" sm="4" class="text-right">
            <v-btn
              color="primary"
              prepend-icon="mdi-download"
              class="mr-2"
              @click="exportToCSV"
            >
              Export CSV
            </v-btn>
            <v-btn
              variant="elevated"
              color="blue"
              size="large"
              prepend-icon="mdi-plus"
              class="rounded-pill px-6 font-semibold text-white"
              @click="openDialog"
            >
              New Transaction
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>

      <!-- Data table -->
      <v-data-table
        :headers="headers"
        :items="Transactions"
        density="comfortable"
        class="elevation-1"
        hover
        striped
      >
        <!-- Amount column with icon -->
        <template #item.amount="{ item }">
          <v-icon size="14" class="mr-1"></v-icon>
          {{ convertToCurrency(item.amount) }}
        </template>

        <!-- Date with icon -->
        <template #item.transaction_date="{ item }">
          <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
          {{ formatDate(item.transaction_date) }}
        </template>

        <!-- Status chip -->
        <template #item.status="{ item }">
          <v-chip
            :color="item.status === 'completed' ? 'success' : 'warning'"
            dark
            small
          >
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Actions menu -->
        <template #item.actions="{ item }">
          <v-menu offset-y>
            <template #activator="{ props }">
              <v-btn icon small v-bind="props">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="openDialog(item)">
                <v-icon class="me-2">mdi-pencil</v-icon>
                Edit
              </v-list-item>
              <v-list-item @click="deleteItem(item)">
                <v-icon color="error" class="me-2">mdi-delete</v-icon>
                Delete
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- No data -->
        <template #no-data>
          <v-card flat class="text-center pa-4">
            <v-icon size="36" color="grey">mdi-database-remove</v-icon>
            <div class="text-subtitle-2 mt-2">No transactions found</div>
            <v-btn color="black" class="mt-2" @click="getTransactions">
              Reset
            </v-btn>
          </v-card>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title class="font-weight-bold">{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="isValid" lazy-validation>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="editedItem.listingId"
                  :items="listings"
                  item-text="title"
                  item-value="id"
                  label="Listing"
                  dense
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.amount"
                  label="Amount"
                  dense
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.currency"
                  label="Currency"
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.paymentMethod"
                  label="Payment Method"
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.transactionFee"
                  label="Transaction Fee"
                  dense
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.phoneNumber"
                  label="Phone Number"
                  dense
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedItem.transactionReference"
                  label="Reference"
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6">
                <label class="d-block mb-1">Date</label>
                <v-text-field
                  v-model="editedItem.transactionDate"
                  type="date"
                  label="Date"
                  dense
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-autocomplete
                  v-model="editedItem.userId"
                  :items="users"
                  item-text="title"
                  item-value="id"
                  label="User"
                  dense
                />
              </v-col>

              <!-- Date picker -->
              <v-col cols="12" sm="6">
                <label class="d-block mb-1">Time</label>
                <v-text-field
                  v-model="editedItem.transactionTime"
                  type="time"
                  dense
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedItem.status"
                  :items="status"
                  label="Status"
                  dense
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" color="grey" @click="onCancel"> Cancel </v-btn>

          <v-btn text color="primary" :disabled="!isValid" @click="save">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete this transaction?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDelete">Cancel</v-btn>
          <v-btn text color="error" @click="deleteItemConfirm"> Delete </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import TransactionsService from "../services/transaction.service.js";
import ListingService from "../services/listing.service.js";
import UsersService from "../services/user.service.js";
import Datepicker from "vue3-datepicker";

export default {
  components: {
    Datepicker,
    // …your other components…
  },
  data: () => ({
    dialog: false,
    dialogDelete: false,
    dateMenu: false,
    timeMenu: false,
    isValid: false,
    listings: [],
    loading: false,
    search: "",
    statusFilter: "",

    headers: [
      { title: "#", value: "id", sortable: false },
      { title: "Listing", value: "listing.title" },
      { title: "Currency", value: "currency" },
      { title: "Amount", value: "amount" },
      { title: "Payment Method", value: "paymentMethod" },
      { title: "Fee", value: "transactionFee" },
      { title: "Date", value: "transaction_date" },
      { title: "Status", value: "status" },
      { title: "Actions", value: "actions", sortable: false },
    ],

    status: ["completed", "pending"],

    Transactions: [],
    users: [],

    editedIndex: -1,
    editedItem: {
      userId: "",
      listingId: "",
      phoneNumber: "",
      amount: "",
      currency: "",
      paymentMethod: "",
      transactionReference: "",
      transactionFee: "",
      transaction_date: new Date(),
      status: "",
      transactionDate: null, // YYYY-MM-DD
      transactionTime: "", // HH:mm:ss
    },
    defaultItem: {
      userId: "",
      listingId: null,
      phoneNumber: "",
      amount: "",
      currency: "",
      paymentMethod: "",
      transactionReference: "",
      transactionFee: "",
      transaction_date: null,
      status: "",
      transactionDate: null,
      transactionTime: "",
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Transaction" : "Edit Transaction";
    },

    filteredTransactions() {
      if (!this.statusFilter) return this.Transactions;
      return this.Transactions.filter(
        (item) => item.status == this.statusFilter
      );
    },
  },

  created() {
    this.getTransactions();
    this.getusers();
    this.getListings();
  },

  watch: {
    statusFilter() {
      this.getTransactions();
    },
  },

  methods: {
    // fetch with search & status
    getTransactions() {
      const params = {
        search: this.search,
        status: this.statusFilter,
      };
      TransactionsService.index(params).then((r) => {
        this.Transactions = r.data.data;
        console.log("the list", this.Transactions);
      });
    },

    getListings() {
      ListingService.index().then((r) => {
        this.listings = r.data.data.map((l) => ({ id: l.id, title: l.title }));
      });
    },

    getusers() {
      UsersService.index().then((r) => {
        this.users = r.data.data.map((u) => ({ id: u.id, title: u.fullName }));
        console.log("Users", this.users);
      });
    },

    convertToCurrency(val) {
      if (val == null) return "";
      const n = parseFloat(val);
      return isNaN(n)
        ? ""
        : n.toLocaleString("en-US", { minimumFractionDigits: 2 });
    },

    formatDate(d) {
      return d ? dayjs(d).format("YYYY-MM-DD") : "";
    },

    openDialog(item = null) {
      if (item) {
        this.editedIndex = this.Transactions.findIndex((t) => t.id === item.id);

        // backend gives you "YYYY-MM-DD HH:mm:ss"
        const [datePart = "", timePart = "00:00:00"] = (
          item.transaction_date || ""
        ).split(" ");

        this.editedItem = {
          ...this.defaultItem,
          // primitive fields
          id: item.id,
          amount: item.amount,
          currency: item.currency,
          paymentMethod: item.paymentMethod,
          transactionReference: item.transactionReference,
          transactionFee: item.transactionFee,
          status: item.status,
          phoneNumber: item.phoneNumber,

          // relations by ID
          listingId: item.listing?.id || null,
          userId: item.user?.id || null,

          // plain strings for native inputs
          transactionDate: datePart, // "2025-04-23"
          transactionTime: timePart.slice(0, 5), // "14:30"
        };
      } else {
        this.editedIndex = -1;
        this.editedItem = { ...this.defaultItem };
      }

      this.dialog = true;
    },

    async save() {
      // 1. Validate form
      this.$refs.form.validate();
      if (!this.isValid) return;

      // 2. Turn on loading
      this.loading = true;

      // 3. Build payload
      const payload = { ...this.editedItem };
      payload.transaction_date = `${payload.transactionDate} ${payload.transactionTime}:00`;
      delete payload.transactionDate;
      delete payload.transactionTime;

      try {
        let response;
        if (this.editedIndex > -1) {
          // Update existing
          response = await TransactionsService.update(payload);
        } else {
          // Create new
          response = await TransactionsService.store(payload);
        }

        // 4. Dispatch success alert
        const code = response.status;
        const msg = response.data.message;
        if (code === 201) {
          this.$store.dispatch("alert/success", msg);
        } else {
          this.$store.dispatch("alert/success", msg || "Transaction saved");
        }

        // 5. Close & reset
        this.onCancel();

        // 6. Re-fetch the full list so the table is up-to-date
        this.getTransactions();
      } catch (error) {
        // 7. Dispatch error alert
        const status = error.response?.status;
        const text = error.response?.data?.message || error.message;
        this.$store.dispatch(
          status === 422 ? "alert/error" : "alert/error",
          text
        );
      } finally {
        // 8. Turn off loading
        this.loading = false;
      }
    },
    // editItem(item) {
    //   this.editedIndex = this.Transactions.indexOf(item);
    // this.editedItem = {
    //   ...this.defaultItem,
    //   ...item,
    //   // pick out the IDs for your selects/autocomplete
    //   listingId: item.listing?.id   || null,
    //   userId:    item.user   ?.id   || null,
    // };
    // this.dialog = true;
    // },

    deleteItem(item) {
      this.editedIndex = this.Transactions.findIndex((t) => t.id === item.id);
      this.editedItem = { ...item };
      this.dialogDelete = true;
    },

    closeDelete() {
      this.dialogDelete = false;
    },

    deleteItemConfirm() {
      TransactionsService.delete(this.editedItem).then(() => {
        this.Transactions.splice(this.editedIndex, 1);
        this.dialogDelete = false;
      });
    },

    onCancel() {
      // 1. close the dialog
      this.dialog = false;

      // 2. reset the form & state on next tick
      this.$nextTick(() => {
        // if you have validation, clear it:
        if (this.$refs.form && this.$refs.form.resetValidation) {
          this.$refs.form.resetValidation();
        }

        // reset your editedItem and index
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
        this.isValid = false;
      });
    },

    exportToCSV() {
      if (!this.Transactions.length) return;
      const rows = [
        Object.keys(this.Transactions[0]).join(","),
        ...this.Transactions.map((obj) =>
          Object.values(obj)
            .map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`)
            .join(",")
        ),
      ];
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "transactions.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.v-toolbar {
  background: #fafafa;
}
</style>
